import { DocumentEventsService } from './shared/services/document-events/document-events.service';
import { Component, DestroyRef, HostListener, inject, NgZone, OnInit, ViewContainerRef } from '@angular/core';
import { AuthService } from './core/services/auth/auth.service';
import { LoaderService } from './shared/services/loader/loader.service';
import { Router, RouterOutlet } from '@angular/router';
import { LoaderComponent } from './shared/components/ui/loader/loader.component';
import { AppSettingsService } from './core/services/app-settings/app-settings.service';
import { validateEnvironment } from './core/functions/validate-environment';
import { configureSentry } from './core/functions/configure-sentry';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { userSettingsFeature } from './store/features/user-settings/user-settings-feature';
import { LocalizationService } from './core/services/localization/localization.service';
import { ViewStackService } from './shared/services/view-stack/view-stack.service';
import { BizzMineSessionStorageService } from './shared/services/localStorage/bizzmine-session-storage.service';
import { SignalRService } from './core/signalR/signal-r.service';
import { NetworkService } from './core/services/network/network.service';
import { PwaService } from './core/services/pwa/pwa.service';
import { Platform } from '@angular/cdk/platform';
import { UserSessionsService } from './core/services/user-sessions/user-sessions.service';
import { NavigationHistoryService } from './core/history/navigation-history.service';
import { SidebarService } from 'src/app/shared/services/sidebar/sidebar.service';
import { IndexedDBService } from './core/services/indexed-db/indexed-db.service';
import { KeepOfflineService } from './core/services/keep-offline/keep-offline.service';
import { race, take, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { networkActions } from './store/features/network/network-actions';
import { Actions, ofType } from '@ngrx/effects';
import { WorkspaceOfflineService } from './core/services/workspace-offline/workspace-offline.service';
import { SyncService } from './core/services/sync/sync.service';


@Component({
  selector: 'bizz-root',
  templateUrl: './bizz.component.html',
  styleUrls: ['./bizz.component.scss'],
  standalone: true,
  imports: [LoaderComponent, RouterOutlet]
})
export class BizzComponent implements OnInit {
  public title = 'BizzMineAngular';

  public showLoader: boolean;
  private sidebarService = inject(SidebarService);

  public constructor(
    private store$: Store,
    private localizationService: LocalizationService,
    private authService: AuthService,
    private destroyRef: DestroyRef,
    private loaderService: LoaderService,
    private appSettingsService: AppSettingsService,
    private sessionStorage: BizzMineSessionStorageService,
    private signalRService: SignalRService,
    private viewStackService: ViewStackService,
    private router: Router,
    public networkService: NetworkService,
    public pwaService: PwaService,
    public platform: Platform,
    public viewContainerRef: ViewContainerRef,
    private keepOfflineService: KeepOfflineService,
    public userSessionsService: UserSessionsService,
    private navigationHistoryService: NavigationHistoryService,
    private documentEventsService: DocumentEventsService,
    private indexeddbService: IndexedDBService,
    private ngZone: NgZone,
    private actions$: Actions,
    private workspaceOfflineService: WorkspaceOfflineService,
    private syncService: SyncService,
  ) {
    this.pwaService.init();
    // pick the landing page -- only 1 time on start up - never again
    const foundRequestedUrl = this.sessionStorage.getItem('REQUESTED_URL');
    if (foundRequestedUrl == null) {
      const path = window.location.pathname?.replace('#', '') + window.location.search;
      if (path != '/' && !path?.includes('auth') && !path.startsWith('/?')) {
        this.sessionStorage.set('REQUESTED_URL', path);
      }
    }

    this.indexeddbService.init();
    if (this.pwaService.isPwa()) {
      race(
        this.actions$.pipe(ofType(networkActions.statusChanged), take(1)),
        timer(3000).pipe(map(() => networkActions.statusChanged({ isOnline: navigator.onLine })))
      ).subscribe({
        next: (action) => {
          this.networkService.isOnline = action.isOnline;
          this.configureApplication();
        }
      });
      this.networkService.checkNetworkStatus(true);
    } else {
      this.networkService.checkNetworkStatus();
      this.configureApplication(true);
    }

    this.navigationHistoryService.init(this.destroyRef);
    this.sidebarService.sidebarInit();
  }

  public configureApplication(disableOnlineCheck: boolean = false): void {
    validateEnvironment();
    this.initializeSignalR(disableOnlineCheck);
    this.userSessionsService.init();
    configureSentry();
    this.initializeOAuth(disableOnlineCheck);

  }

  public ngOnInit(): void {
    this.loaderService.getObservable().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: value => {
        this.showLoader = value;
      },
    });

    this.store$.select(userSettingsFeature.selectUserSettingsState).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: userSettings => {
        if (userSettings.UserLanguagesID !== 0 && userSettings.CompanyLanguagesID !== 0) {
          this.localizationService.updateLocaleWithUserSettings(userSettings);
        }
      },
    });
  }

  @HostListener('document:click', ['$event'])
  public documentClick(event: KeyboardEvent): void {
    return this.ngZone.runOutsideAngular(() =>
      this.documentEventsService.documentClickEvent.next(event),
    );
  }

  @HostListener('document:keydown', ['$event'])
  public documentKeydown(event: KeyboardEvent): void {
    return this.ngZone.runOutsideAngular(() =>
      this.documentEventsService.documentKeydownEvent.next(event),
    );
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: Event): void {
    this.documentEventsService.windowResizeEvent.next(event);
  }

  private initializeSignalR(disableOnlineCheck: boolean): void {
    if (this.networkService.isOnline || disableOnlineCheck) {
      this.signalRService.initApiHub();
      this.signalRService.initIdpHub();
      this.signalRService.initAiHub();
    }
  }

  private initializeOAuth(disableOnlineCheck: boolean): void {
    console.debug('Initializing OAuth...', this.networkService.isOnline);
    if (this.networkService.isOnline || disableOnlineCheck) {
      this.authService.configureIdentityServer();
    } else {
      this.authService._oAuthInitialized.next(true);
    }
  }

}
